import countries from '../data/countries';

const MAX_SUGGESTIONS = 4;

// Shown when little or no filter context is selected; also tops up the list
// when the selected context yields fewer than MAX_SUGGESTIONS prompts.
const FALLBACK_SUGGESTIONS = [
    'Best cities for my study field',
    'Companies hiring in English',
    'Student opportunities by country',
    'Compare two cities',
];

const cleanLabel = (value) => {
    const str = String(value ?? '').trim();
    if (!str || str === 'undefined' || str === 'null') return '';
    return str;
};

// NACE fallback labels can be bare codes like "62" or "62.01" when the
// industry list hasn't resolved them; those must never appear in a prompt.
const isCodeLikeLabel = (label) => /^\d+(?:\.\d+)?$/.test(String(label).trim());

// Resolves an ISO2 code or a display name to the canonical display name.
// Returns '' when unknown so raw codes never leak into prompts.
const resolveCountryName = (value) => {
    const normalized = String(value || '').trim();
    if (!normalized) return '';

    const upper = normalized.toUpperCase();
    const lower = normalized.toLowerCase();

    const match = countries.find(
        (country) =>
            String(country.id || '').trim().toUpperCase() === upper ||
            String(country.name || '').trim().toLowerCase() === lower
    );

    return match ? match.name : '';
};

// Derives up to MAX_SUGGESTIONS deterministic Ask Abroadify prompts from the
// Discovery page's current context. Priority: company, city, country, study
// direction, industry, English/student/traveler context, generic fallback.
export const getDiscoveryAiSuggestions = ({
    discoveryType = 'jobs', // eslint-disable-line no-unused-vars -- reserved for per-page variants
    companyName = '',
    selectedCountry = '',
    selectedCity = '',
    selectedIndustry = '',
    selectedStudyDirection = '',
    selectedEnglishSpeaking = null,
    selectedWorldTraveler = null,
    selectedStudentJobAreas = null,
    industryNameByCode = new Map(),
} = {}) => {
    const company = cleanLabel(companyName);
    const city = cleanLabel(selectedCity);
    const country = resolveCountryName(selectedCountry);
    const studyDirection = cleanLabel(selectedStudyDirection);

    // selectedIndustry may be a NACE code (resolved via the map) or an
    // already-human-readable AI group label (passes through unchanged).
    const rawIndustryLabel = cleanLabel(
        industryNameByCode.get(String(selectedIndustry)) || selectedIndustry
    );
    const industry = isCodeLikeLabel(rawIndustryLabel) ? '' : rawIndustryLabel;

    const suggestions = [];
    const add = (suggestion) => {
        const label = cleanLabel(suggestion);
        if (label && !suggestions.includes(label)) {
            suggestions.push(label);
        }
    };

    if (company) {
        add(`Does ${company} hire international students?`);
        add(`What careers are available at ${company}?`);
        add(`Is English required at ${company}?`);
        if (city) {
            add(`Similar companies in ${city}`);
        } else if (country) {
            add(`Similar companies in ${country}`);
        } else {
            add(`Companies similar to ${company}`);
        }
    }

    if (city) {
        add(`Companies hiring in ${city}`);
        add(`Student opportunities in ${city}`);
        add(`English-speaking employers in ${city}`);
        add(`Compare ${city} with another city`);
    }

    if (country) {
        add(`Best cities in ${country}`);
        add(`Top employers in ${country}`);
        add(`Student opportunities in ${country}`);
        add(`English-speaking companies in ${country}`);
    }

    if (studyDirection) {
        add(`Best cities for ${studyDirection}`);
        add(`${studyDirection} internships`);
        add(`Companies hiring ${studyDirection} graduates`);
    }

    if (industry) {
        add(`Top ${industry} companies`);
        add(`Best cities for ${industry} jobs`);
        add(`${industry} internships`);
        add(`English-speaking ${industry} employers`);
    }

    if (selectedEnglishSpeaking === true) {
        add('English-speaking companies');
        add('Best cities for English-speaking jobs');
    }

    // One prompt for student job areas — first area label when a list is
    // selected, a generic prompt when it's the boolean page toggle.
    const firstStudentJobArea = Array.isArray(selectedStudentJobAreas)
        ? cleanLabel(selectedStudentJobAreas.find((area) => cleanLabel(area)))
        : '';

    if (firstStudentJobArea) {
        add(`Student jobs in ${firstStudentJobArea}`);
    } else if (selectedStudentJobAreas === true) {
        add('Companies with student job areas');
    }

    if (selectedWorldTraveler === true) {
        add('Companies open to world travelers');
    }

    FALLBACK_SUGGESTIONS
        // Near-duplicate of 'English-speaking companies' when the toggle is on.
        .filter((s) => !(selectedEnglishSpeaking === true && s === 'Companies hiring in English'))
        .forEach(add);

    return suggestions.slice(0, MAX_SUGGESTIONS);
};
